import { useState, useCallback, useMemo, useEffect } from "react";
import { useLocation } from "wouter";
import { Plus, Users, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useImpersonation } from "@/context/ImpersonationContext";
import { useCurrentTramsContext } from "@/hooks/useTramsAccounts";
import { isAgencyOwnerRole } from "@/lib/roles";
import { useCompanyAccess } from "@/hooks/useCompanyAccess";
import { TramsAccountSwitcher } from "@/components/trams/TramsAccountSwitcher";

import { SectionErrorBoundary, SectionScopeContext } from "@/components/invoicing/SectionErrorBoundary";
import type { SectionScopeInfo } from "@/components/invoicing/SectionErrorBoundary";
import { CommissionKpis } from "@/components/invoicing/CommissionKpis";
import { GoalSection } from "@/components/goals/GoalSection";
// import { AiviaRecommends } from "@/components/invoicing/AiviaRecommends";
import { RecentTripCards } from "@/components/invoicing/RecentTripCards";
import { InvoiceLogTable } from "@/components/invoicing/InvoiceLogTable";
import { ImportTramsHistoryButton } from "@/components/invoicing/ImportTramsHistoryButton";
import { FINANCES_PATHS } from "@/lib/financesPaths";

export default function Invoicing() {
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const { impersonation } = useImpersonation();
  const { effectiveRole, hasCompanyAccess } = useCompanyAccess();
  const { data: tramsData } = useCurrentTramsContext();
  const currentTramsAccountId = tramsData?.context?.tramsAccountId;

  // Single page-level view mode — Owner/Manager defaults to company ("all")
  const [viewMode, setViewMode] = useState<"my" | "all">(
    hasCompanyAccess ? "all" : "my",
  );

  useEffect(() => {
    setViewMode(hasCompanyAccess ? "all" : "my");
  }, [hasCompanyAccess]);

  const handleViewModeChange = useCallback((mode: "my" | "all") => {
    setViewMode(mode);
  }, []);

  const scopeContext = useMemo<SectionScopeInfo>(
    () => ({
      memberId: impersonation.active ? impersonation.targetUserId ?? user?.id : user?.id,
      tramsAccountId: currentTramsAccountId,
      role: effectiveRole ?? undefined,
      viewMode,
    }),
    [impersonation.active, impersonation.targetUserId, user?.id, effectiveRole, currentTramsAccountId, viewMode],
  );

  return (
    <SectionScopeContext.Provider value={scopeContext}>
    <div className="p-4 sm:p-6 max-w-[1600px] mx-auto space-y-6">
      {/* Page Header — single control bar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Invoices</h1>
          {/* AR-316 — v9 gives every Finances page a one-line context subtitle
              under the title. */}
          <p className="mt-0.5 text-sm text-muted-foreground">
            {viewMode === "all" ? "All advisor invoices" : "Your invoices"} ·
            commission counted year-to-date
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {/* Team / Personal toggle — right next to TRAMS switcher */}
          {hasCompanyAccess && (
            <div className="flex rounded-md bg-muted p-0.5" role="tablist" aria-label="Dashboard scope">
              <button
                role="tab"
                aria-selected={viewMode === "all"}
                className={`flex min-h-11 items-center gap-1.5 rounded px-3 py-1.5 text-xs font-medium transition-colors md:min-h-0 ${
                  viewMode === "all"
                    ? "bg-background shadow-sm text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                onClick={() => handleViewModeChange("all")}
              >
                <Users className="h-3.5 w-3.5" aria-hidden />
                Team
              </button>
              <button
                role="tab"
                aria-selected={viewMode === "my"}
                className={`flex min-h-11 items-center gap-1.5 rounded px-3 py-1.5 text-xs font-medium transition-colors md:min-h-0 ${
                  viewMode === "my"
                    ? "bg-background shadow-sm text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                onClick={() => handleViewModeChange("my")}
              >
                <User className="h-3.5 w-3.5" aria-hidden />
                Personal
              </button>
            </div>
          )}
          {/* Team view follows the selected account for owners (split by entity when
              they hold 2+ TRAMS accounts); other team viewers see all accounts. */}
          <TramsAccountSwitcher disabled={viewMode === "all" && !isAgencyOwnerRole(effectiveRole)} />
          {viewMode === "my" && (
            <ImportTramsHistoryButton tramsAccountId={currentTramsAccountId} />
          )}
          <Button onClick={() => setLocation(FINANCES_PATHS.invoicesNew)}>
            <Plus className="h-4 w-4 mr-2" />
            New Invoice
          </Button>
        </div>
      </div>

      {/* Commission Performance KPIs */}
      <SectionErrorBoundary sectionName="Commission Performance">
        <CommissionKpis
          tramsAccountId={currentTramsAccountId}
          viewMode={viewMode}
        />
      </SectionErrorBoundary>

      {/* Goal Tracker — driven by page-level viewMode */}
      <SectionErrorBoundary sectionName="Goal Tracker">
        <GoalSection viewMode={viewMode} />
      </SectionErrorBoundary>

      {/* AIVIA Recommends — hidden placeholder, re-enable when ready */}
      {/* <SectionErrorBoundary sectionName="AIVIA Recommends">
        <AiviaRecommends />
      </SectionErrorBoundary> */}

      {/* Recent Trips */}
      <SectionErrorBoundary sectionName="Recent Trips">
        <RecentTripCards />
      </SectionErrorBoundary>

      {/* Invoice Log Table */}
      <SectionErrorBoundary sectionName="Invoice Log">
        <InvoiceLogTable
          tramsAccountId={currentTramsAccountId}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          hasCompanyAccess={hasCompanyAccess}
        />
      </SectionErrorBoundary>
    </div>
    </SectionScopeContext.Provider>
  );
}
